import { supabase, isSupabaseConfigured, mapUserToSupabase, mapSupabaseToUser, SupabaseUser } from '../lib/supabaseClient';
import { User } from '../types';

/**
 * CONFIGURACIÓN DE SUPABASE STORAGE
 */
const STORAGE_BUCKET = 'profile-images';
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

/**
 * SERVICIO DE SUPABASE
 * 
 * Este archivo centraliza todas las operaciones de base de datos.
 * 
 * PARA DESARROLLADORES JUNIOR:
 * - Un "servicio" es una clase que agrupa funciones relacionadas
 * - Separamos la lógica de base de datos de los componentes React
 * - Esto hace el código más fácil de mantener y testear
 */

export class SupabaseService {
  
  /**
   * Sube una imagen de perfil a Supabase Storage
   * 
   * @param file - Archivo de imagen a subir
   * @param userId - ID del usuario (para organizar las imágenes)
   * @returns Promise con la URL pública de la imagen o error
   */
  static async uploadProfileImage(
    file: File, 
    userId: string
  ): Promise<{ success: boolean; url?: string; error?: string }> {
    if (!isSupabaseConfigured()) {
      return { 
        success: false, 
        error: 'Supabase no está configurado. Las imágenes no se pueden subir.' 
      };
    }

    try {
      console.log('📤 Iniciando subida de imagen a Supabase Storage...');
      console.log('📋 Detalles del archivo:', {
        name: file.name,
        size: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
        type: file.type
      });

      // Validar tipo de archivo
      if (!ALLOWED_TYPES.includes(file.type)) {
        return {
          success: false,
          error: `Tipo de archivo no permitido. Usa: ${ALLOWED_TYPES.join(', ')}`
        };
      }

      // Validar tamaño de archivo
      if (file.size > MAX_FILE_SIZE) {
        return {
          success: false,
          error: `Archivo demasiado grande. Máximo: ${MAX_FILE_SIZE / 1024 / 1024}MB`
        };
      }

      // Generar nombre único para el archivo
      const fileExtension = file.name.split('.').pop() || 'jpg';
      const fileName = `${userId}-${Date.now()}.${fileExtension}`;
      const filePath = `profiles/${fileName}`;

      console.log('📁 Subiendo archivo:', filePath);

      // Subir archivo a Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true // Sobrescribir si ya existe
        });

      if (uploadError) {
        console.error('❌ Error al subir archivo:', uploadError);
        
        // Mensajes de error más amigables
        let friendlyError = uploadError.message;
        if (uploadError.message.includes('Bucket not found')) {
          friendlyError = 'El bucket de almacenamiento no existe. Configura Supabase Storage.';
        } else if (uploadError.message.includes('Insufficient permissions')) {
          friendlyError = 'Sin permisos para subir archivos. Revisa las políticas de Storage.';
        }
        
        return { success: false, error: friendlyError };
      }

      console.log('✅ Archivo subido exitosamente:', uploadData.path);

      // Obtener URL pública del archivo
      const { data: urlData } = supabase.storage
        .from(STORAGE_BUCKET)
        .getPublicUrl(filePath);

      if (!urlData.publicUrl) {
        return {
          success: false,
          error: 'No se pudo obtener la URL pública de la imagen'
        };
      }

      console.log('🔗 URL pública generada:', urlData.publicUrl);

      return {
        success: true,
        url: urlData.publicUrl
      };

    } catch (error) {
      console.error('❌ Error inesperado al subir imagen:', error);
      return {
        success: false,
        error: `Error de conexión: ${error instanceof Error ? error.message : 'Error desconocido'}`
      };
    }
  }

  /**
   * Elimina una imagen de perfil de Supabase Storage
   * 
   * @param imageUrl - URL de la imagen a eliminar
   * @returns Promise con el resultado de la operación
   */
  static async deleteProfileImage(imageUrl: string): Promise<{ success: boolean; error?: string }> {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase no está configurado' };
    }

    try {
      // Extraer el path del archivo de la URL
      const url = new URL(imageUrl);
      const pathParts = url.pathname.split('/');
      const filePath = pathParts.slice(-2).join('/'); // profiles/filename.jpg

      console.log('🗑️ Eliminando imagen:', filePath);

      const { error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .remove([filePath]);

      if (error) {
        console.error('❌ Error al eliminar imagen:', error);
        return { success: false, error: error.message };
      }

      console.log('✅ Imagen eliminada exitosamente');
      return { success: true };

    } catch (error) {
      console.error('❌ Error inesperado al eliminar imagen:', error);
      return { success: false, error: 'Error de conexión' };
    }
  }

  /**
   * Guarda un usuario en Supabase
   * 
   * EXPLICACIÓN:
   * - upsert = update + insert (actualiza si existe, crea si no existe)
   * - onConflict especifica qué hacer si ya existe un registro con el mismo ID
   * 
   * @param user - Usuario a guardar
   * @returns Promise con el resultado de la operación
   */
  static async saveUser(user: User): Promise<{ success: boolean; error?: string; data?: any }> {
    // Verificar si Supabase está configurado
    if (!isSupabaseConfigured()) {
      console.warn('⚠️ Supabase no configurado - operación cancelada');
      console.info('💡 Verifica que tu archivo .env contenga:');
      console.info('   VITE_SUPABASE_URL=https://tu-proyecto.supabase.co');
      console.info('   VITE_SUPABASE_ANON_KEY=tu-clave-aqui');
      console.info('🔄 Reinicia el servidor de desarrollo después de configurar las variables');
      return { 
        success: false, 
        error: 'Supabase no está configurado. Revisa tu archivo .env y reinicia el servidor.' 
      };
    }

    try {
      console.log('💾 Iniciando guardado de usuario en Supabase...');
      console.log('📋 Datos del usuario:', { 
        id: user.id, 
        name: user.name, 
        userType: user.userType,
        location: user.location 
      });
      
      // Convertir el usuario al formato de Supabase
      const supabaseUserData = mapUserToSupabase(user);
      
      console.log('🔄 Datos convertidos para Supabase:', {
        id: supabaseUserData.id,
        name: supabaseUserData.name,
        user_type: supabaseUserData.user_type,
        totalFields: Object.keys(supabaseUserData).length
      });
      
      // Realizar la operación upsert
      const { data, error } = await supabase
        .from('users')
        .upsert([supabaseUserData], { 
          onConflict: 'id',
          ignoreDuplicates: false 
        })
        .select();

      if (error) {
        console.error('❌ Error detallado de Supabase:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        
        // Proporcionar mensajes de error más amigables
        let friendlyError = `Error de base de datos: ${error.message}`;
        
        if (error.message.includes('Invalid API key') || error.code === '401') {
          friendlyError = 'Credenciales de Supabase inválidas. Revisa tu archivo .env y reinicia el servidor.';
        } else if (error.message.includes('relation "users" does not exist')) {
          friendlyError = 'La tabla "users" no existe. Ejecuta las migraciones de base de datos.';
        } else if (error.message.includes('RLS') || error.message.includes('policy')) {
          friendlyError = 'Error de permisos. Revisa las políticas de seguridad (RLS).';
        } else if (error.code === 'PGRST301') {
          friendlyError = 'Error de validación de datos. Verifica que todos los campos requeridos estén presentes.';
        }
        
        return { success: false, error: friendlyError };
      }

      console.log('✅ Usuario guardado exitosamente en Supabase!');
      console.log('📊 Datos guardados:', data?.[0] ? {
        id: data[0].id,
        name: data[0].name,
        user_type: data[0].user_type,
        created_at: data[0].created_at
      } : 'Sin datos de respuesta');
      
      return { success: true, data };
      
    } catch (error) {
      console.error('❌ Error inesperado al guardar usuario:', {
        error: error instanceof Error ? error.message : error,
        stack: error instanceof Error ? error.stack : undefined
      });
      return { 
        success: false, 
        error: `Error de conexión: ${error instanceof Error ? error.message : 'Error desconocido'}` 
      };
    }
  }

  /**
   * Obtiene un usuario por ID desde Supabase
   * 
   * @param userId - ID del usuario a buscar
   * @returns Promise con el usuario encontrado o null
   */
  static async getUserById(userId: string): Promise<{ success: boolean; user?: User; error?: string }> {
    if (!isSupabaseConfigured()) {
      return { 
        success: false, 
        error: 'Supabase no está configurado' 
      };
    }

    try {
      console.log('🔍 Buscando usuario en Supabase:', userId);
      
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No se encontró el usuario
          return { success: false, error: 'Usuario no encontrado' };
        }
        
        console.error('❌ Error al buscar usuario:', error);
        return { success: false, error: error.message };
      }

      // Convertir de formato Supabase a formato frontend
      const user = mapSupabaseToUser(data as SupabaseUser);
      
      console.log('✅ Usuario encontrado:', user.name);
      return { success: true, user };
      
    } catch (error) {
      console.error('❌ Error inesperado al buscar usuario:', error);
      return { 
        success: false, 
        error: 'Error de conexión' 
      };
    }
  }

  /**
   * Obtiene todos los usuarios (para el feed de swipe)
   * 
   * @param excludeUserId - ID del usuario actual (para no mostrarlo en el feed)
   * @param userType - Tipo de usuario a buscar (opcional)
   * @param limit - Número máximo de usuarios a retornar
   * @returns Promise con la lista de usuarios
   */
  static async getUsers(
    excludeUserId?: string, 
    userType?: 'entrepreneur' | 'investor',
    limit: number = 50
  ): Promise<{ success: boolean; users?: User[]; error?: string }> {
    if (!isSupabaseConfigured()) {
      console.warn('⚠️ Supabase no configurado - no se pueden cargar usuarios');
      return { 
        success: false, 
        error: 'Supabase no está configurado. Verifica tu archivo .env.' 
      };
    }

    try {
      console.log('📋 Obteniendo usuarios desde Supabase...');
      console.log('🔍 Parámetros:', { excludeUserId, userType, limit });
      
      // Construir consulta paso a paso para evitar errores 406
      let query = supabase
        .from('users')
        .select('*');

      // Excluir usuario actual si se especifica
      if (excludeUserId) {
        query = query.not('id', 'eq', excludeUserId);
        console.log('🚫 Excluyendo usuario:', excludeUserId);
      }

      // Filtrar por tipo de usuario si se especifica
      if (userType) {
        query = query.eq('user_type', userType);
        console.log('🎯 Filtrando por tipo:', userType);
      }

      // Aplicar ordenamiento y límite al final
      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('❌ Error al obtener usuarios:', {
          message: error.message,
          code: error.code,
          details: error.details
        });
        
        // Si es error 406, devolver array vacío para que la app siga funcionando
        if (error.code === '406' || error.message.includes('Not Acceptable')) {
          console.warn('⚠️ Error 406 - Posible problema con políticas RLS. Devolviendo array vacío.');
          return { success: true, users: [] };
        }
        
        return { success: false, error: error.message };
      }

      if (!data || data.length === 0) {
        console.warn('⚠️ No se encontraron usuarios en la base de datos');
        return { 
          success: true, 
          users: [],
          error: 'No hay usuarios disponibles en este momento'
        };
      }

      // Convertir todos los usuarios al formato frontend
      const users = data.map((supabaseUser: SupabaseUser) => 
        mapSupabaseToUser(supabaseUser)
      );
      
      console.log(`✅ ${users.length} usuarios obtenidos desde Supabase:`, {
        entrepreneurs: users.filter(u => u.userType === 'entrepreneur').length,
        investors: users.filter(u => u.userType === 'investor').length,
        total: users.length,
        userIds: users.map(u => ({ id: u.id.substring(0, 20) + '...', name: u.name, type: u.userType }))
      });
      
      return { success: true, users };
      
    } catch (error) {
      console.error('❌ Error inesperado al obtener usuarios:', error);
      return { 
        success: false, 
        error: `Error de conexión: ${error instanceof Error ? error.message : 'Error desconocido'}` 
      };
    }
  }

  /**
   * Actualiza la información de un usuario
   * 
   * @param userId - ID del usuario a actualizar
   * @param updates - Campos a actualizar
   * @returns Promise con el resultado de la operación
   */
  static async updateUser(
    userId: string, 
    updates: Partial<User>
  ): Promise<{ success: boolean; error?: string; data?: any }> {
    if (!isSupabaseConfigured()) {
      return { 
        success: false, 
        error: 'Supabase no está configurado' 
      };
    }

    try {
      console.log('🔄 Actualizando usuario en Supabase:', userId);
      
      // Convertir las actualizaciones al formato Supabase
      const supabaseUpdates = mapUserToSupabase({ id: userId, ...updates } as User);
      
      // Añadir timestamp de actualización
      supabaseUpdates.updated_at = new Date().toISOString();
      
      const { data, error } = await supabase
        .from('users')
        .update(supabaseUpdates)
        .eq('id', userId)
        .select();

      if (error) {
        console.error('❌ Error al actualizar usuario:', error);
        return { success: false, error: error.message };
      }

      console.log('✅ Usuario actualizado exitosamente');
      return { success: true, data };
      
    } catch (error) {
      console.error('❌ Error inesperado al actualizar usuario:', error);
      return { 
        success: false, 
        error: 'Error de conexión' 
      };
    }
  }

  /**
   * Elimina un usuario (soft delete - marca como inactivo)
   * 
   * NOTA: En lugar de eliminar completamente, es mejor práctica
   * marcar como inactivo para mantener integridad referencial
   * 
   * @param userId - ID del usuario a eliminar
   * @returns Promise con el resultado de la operación
   */
  static async deleteUser(userId: string): Promise<{ success: boolean; error?: string }> {
    if (!isSupabaseConfigured()) {
      return { 
        success: false, 
        error: 'Supabase no está configurado' 
      };
    }

    try {
      console.log('🗑️ Eliminando usuario:', userId);
      
      // En lugar de DELETE, actualizamos un campo 'active' a false
      // Esto requiere añadir el campo 'active' a la tabla si no existe
      const { error } = await supabase
        .from('users')
        .update({ 
          updated_at: new Date().toISOString(),
          // active: false // Descomenta si añades este campo
        })
        .eq('id', userId);

      if (error) {
        console.error('❌ Error al eliminar usuario:', error);
        return { success: false, error: error.message };
      }

      console.log('✅ Usuario eliminado exitosamente');
      return { success: true };
      
    } catch (error) {
      console.error('❌ Error inesperado al eliminar usuario:', error);
      return { 
        success: false, 
        error: 'Error de conexión' 
      };
    }
  }

  /**
   * Verifica si un usuario existe en la base de datos
   * 
   * @param userId - ID del usuario a verificar
   * @returns Promise<boolean> - true si existe, false si no
   */
  static async userExists(userId: string): Promise<boolean> {
    if (!isSupabaseConfigured()) {
      return false;
    }

    try {
      const { data, error } = await supabase
        .from('users')
        .select('id')
        .eq('id', userId)
        .single();

      return !error && !!data;
    } catch {
      return false;
    }
  }

  /**
   * Mapea usuario de Supabase a formato frontend
   */
  static mapSupabaseUserToFrontend(supabaseUser: any): User {
    // Mapeo directo
    const baseUser = {
      id: supabaseUser.id,
      name: supabaseUser.name || '',
      email: supabaseUser.email || '',
      userType: supabaseUser.user_type || 'entrepreneur',
      profileImage: supabaseUser.profile_image || '',
      bio: supabaseUser.bio || '',
      location: supabaseUser.location || '',
      website: supabaseUser.website || '',
      linkedin: supabaseUser.linkedin || '',
      twitter: supabaseUser.twitter || '',
      github: supabaseUser.github || '',
      skills: supabaseUser.skills || [],
      interests: supabaseUser.interests || [],
      experience: supabaseUser.experience || '',
      education: supabaseUser.education || '',
      languages: supabaseUser.languages || [],
      availability: supabaseUser.availability || 'full-time',
      remoteWork: supabaseUser.remote_work || false,
      salary: supabaseUser.salary || '',
      equity: supabaseUser.equity || false,
      companyStage: supabaseUser.company_stage || '',
      fundingAmount: supabaseUser.funding_amount || '',
      industry: supabaseUser.industry || '',
      businessModel: supabaseUser.business_model || '',
      targetMarket: supabaseUser.target_market || '',
      competition: supabaseUser.competition || '',
      revenue: supabaseUser.revenue || '',
      team: supabaseUser.team || '',
      investmentRange: supabaseUser.investment_range || '',
      investmentStage: supabaseUser.investment_stage || '',
      investmentSectors: supabaseUser.investment_sectors || [],
      portfolioCompanies: supabaseUser.portfolio_companies || '',
      investmentCriteria: supabaseUser.investment_criteria || '',
      mentorship: supabaseUser.mentorship || false,
      networkValue: supabaseUser.network_value || '',
      riskTolerance: supabaseUser.risk_tolerance || 'medium',
      exitStrategy: supabaseUser.exit_strategy || '',
      createdAt: supabaseUser.created_at || new Date().toISOString(),
      updatedAt: supabaseUser.updated_at || new Date().toISOString()
    };

    return baseUser;
  }
}

/**
 * FUNCIONES DE UTILIDAD PARA DESARROLLADORES JUNIOR
 */

/**
 * Inicializa la conexión con Supabase y verifica que todo esté funcionando
 * 
 * USO: Llama esta función al inicio de tu aplicación
 */
export const initializeSupabase = async (): Promise<boolean> => {
  console.log('🚀 Inicializando Supabase...');
  
  if (!isSupabaseConfigured()) {
    console.warn('⚠️ Supabase no configurado - la app funcionará en modo local');
    return false;
  }

  try {
    // Probar conexión básica
    const { data, error } = await supabase
      .from('users')
      .select('count')
      .limit(1);

    if (error) {
      console.error('❌ Error al inicializar Supabase:', error.message);
      return false;
    }

    console.log('✅ Supabase inicializado correctamente');
    return true;
  } catch (error) {
    console.error('❌ Error inesperado al inicializar Supabase:', error);
    return false;
  }
};

/**
 * EJEMPLO DE USO PARA DESARROLLADORES JUNIOR:
 * 
 * // En tu componente React:
 * import { SupabaseService } from '../services/supabaseService';
 * 
 * const saveUserProfile = async (user) => {
 *   const result = await SupabaseService.saveUser(user);
 *   
 *   if (result.success) {
 *     console.log('Usuario guardado!');
 *   } else {
 *     console.error('Error:', result.error);
 *   }
 * };
 */